import { useLazyQuery, useMutation } from '@apollo/client';
import { useIsAuthenticated } from '@azure/msal-react';
import { Button, DatePicker, Form, Select, notification } from 'antd';
import React, { useEffect } from 'react';
import { GET_PARTICIPANTS } from '../Party/PartyQueries';
import { ADD_SUBMISSION_MUTATION, GET_SUBMISSION, UPDATE_SUBMISSION } from './SubmissionQueries';
import dayjs from 'dayjs';
import CustomInput from '../CustomComponents/CustomInput';
import { useSearchParams } from 'react-router-dom';

interface SubmissionFormProps {
  onSubmit: (success: boolean) => void;
  onCancel: () => void;
  proceedingId: string;
  participantId: string;
  submissionId: string;
}

interface OptionMap {
  value: string;
  label: string;
}

const { Option } = Select;
const SubmissionForm: React.FC<SubmissionFormProps> = ({
  onSubmit,
  onCancel,
  proceedingId,
  participantId,
  submissionId,
}) => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const today = new Date();
  const isAuthenticated = useIsAuthenticated();
  const isEditForm = submissionId !== null ? true : false;
  const statusOptions = [
    { value: 'Received', label: 'Received' },
    { value: 'Not Received', label: 'Not Received' },
    { value: 'Pending', label: 'Pending' },
  ];
  const participantOptions: OptionMap[] = [];

  const [getParticipants, { data: response, loading, error, refetch }] =
    useLazyQuery(GET_PARTICIPANTS);

  const [addSubmission, { data: addResult, error: addError }] =
    useMutation(ADD_SUBMISSION_MUTATION);
  const [updateSubmission, { loading: updating, data: updateResult, error: updateError }] =
    useMutation(UPDATE_SUBMISSION);

  useEffect(() => {
    if (tab === '4' && proceedingId) {
      getParticipants({
        variables: {
          input: { proceedingNumber: proceedingId },
        },
      });
    }
  }, [tab]);

  response?.participants?.data.map((participant: any, index: number) => {
    participantOptions.push({
      value: participant.id,
      label: `${participant.partyName}`,
    });
  });
  const [getSubmission, { data: data, loading: loadingDetails, error: loadingError }] =
    useLazyQuery(GET_SUBMISSION);

  useEffect(() => {
    form.resetFields();
    if (isEditForm) {
      getSubmission({
        variables: {
          input: {
            proceedingNumber: proceedingId,
            participantId: participantId,
            submissionId: submissionId,
          },
        },
      });
    }
  }, [proceedingId, participantId, submissionId]);

  useEffect(() => {
    if (data?.submission) {
      form.setFieldValue('participant', data?.submission.participantId);
      form.setFieldValue('submissionStatus', data?.submission.submissionStatus);
      form.setFieldValue('description', data?.submission.description);
      form.setFieldValue('submissionDeadlineDate', dayjs(data?.submission.submissionDeadlineDate));
      form.setFieldValue(
        'submissionReceivedDate',
        data?.submission.submissionReceivedDate
          ? dayjs(data?.submission.submissionReceivedDate)
          : null
      );
    }
  }, [data]);

  const handleChange = (dateString: any) => {
    const submissionStatus = dateString
      ? statusOptions.find((status) => status.value === 'Received').label
      : data?.submission?.submissionStatus;
    form.setFieldValue('submissionStatus', submissionStatus);
  };

  const onFinish = async (values: any) => {
    try {
      const participantId =
        typeof values.participant === 'string' ? values.participant : values.participant.value;
      if (isEditForm) {
        await updateSubmission({
          variables: {
            input: {
              id: submissionId,
              participantId,
              proceedingId: proceedingId,
              submissionDeadlineDate: values.submissionDeadlineDate,
              submissionReceivedDate: values.submissionReceivedDate,
              submissionStatus: values.submissionStatus,
              description: values.description,
            },
          },
        });
      } else {
        await addSubmission({
          variables: {
            input: {
              participantId,
              proceedingId: proceedingId,
              submissionDeadlineDate: values.submissionDeadlineDate,
              submissionReceivedDate: values.submissionReceivedDate,
              submissionStatus: values.submissionStatus,
              description: values.description,
            },
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    if (
      isEditForm &&
      updateResult !== undefined &&
      updateResult.updateSubmission.result === 'Success'
    ) {
      notification.success({
        message: 'Success',
        description: `Submission has been updated successfully.`,
      });
      onSubmit(true);
    }
    if (
      isEditForm &&
      updateResult !== undefined &&
      updateResult.updateSubmission.result === 'Error'
    ) {
      notification.error({
        message: 'Error',
        description: `An error occured in updating the submission.`,
      });
      onSubmit(false);
    }
    if (!isEditForm && addResult !== undefined && addResult.addSubmission.result === 'Success') {
      notification.success({
        message: 'Success',
        description: `Submission has been added.`,
      });
      form.resetFields();
      onSubmit(true);
    }
    if (!isEditForm && addResult !== undefined && addResult.addSubmission.result === 'Error') {
      notification.error({
        message: 'Error',
        description: `Error in adding submission to the proceeding.`,
      });
      onSubmit(false);
    }
  }, [updateResult, updateError, addResult, addError]);
  return (
    <>
      <div className="thin-border">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="participant"
            label="Submitter"
            rules={[{ required: true, message: 'Please select an option' }]}
          >
            <Select labelInValue disabled={isEditForm || !isAuthenticated}>
              {participantOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description">
            <CustomInput showInput={true} disabled={!isAuthenticated}></CustomInput>
          </Form.Item>
          <Form.Item
            name="submissionDeadlineDate"
            label="Submission Deadline Date"
            rules={[{ required: true, message: 'Please select the deadline date' }]}
          >
            <DatePicker disabled={!isAuthenticated} style={{ width: '100%' }}></DatePicker>
          </Form.Item>
          <Form.Item name="submissionReceivedDate" label="Submission Received Date">
            <DatePicker
              disabled={!isAuthenticated}
              style={{ width: '100%' }}
              disabledDate={(current) => current.isAfter(today)}
              onChange={handleChange}
            ></DatePicker>
          </Form.Item>
          <Form.Item name="submissionStatus" label="Submission Status">
            <Select disabled={!isAuthenticated} options={statusOptions} />
          </Form.Item>
          {isAuthenticated && (
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={onCancel}>
                Cancel
              </Button>
            </Form.Item>
          )}{' '}
          {!isAuthenticated && (
            <Button style={{ marginLeft: 8 }} onClick={onCancel}>
              Back
            </Button>
          )}
        </Form>
      </div>
    </>
  );
};
export default SubmissionForm;
